import React from 'react';
import { EnrollmentPeriod, InstalledProductLine, InstalledRate } from 'types/enrollmentService';
import { convertRateValue } from 'global/utils/convertRateValue';
import clsx from 'clsx';
import { Download, InfoButton } from '@insurancemenu/imenu-components-lib/dist/icons';
import { Button } from '@insurancemenu/imenu-components-lib/dist/components';
import colors from '@insurancemenu/imenu-components-lib/dist/utils/colors';
import { observer } from 'mobx-react-lite';
import { getUploadExhibitZipUrl } from 'api/operations/carrierFileService';
import { downloadByUrl } from '@insurancemenu/imenu-components-lib/dist/helpers/downloadByUrl';
import { createStyles } from './styles';
import { hasRenewalRates } from '../../utils';

export interface ProductLineDataProps {
  productLineData: InstalledProductLine;
  enrollmentPeriod: Pick<EnrollmentPeriod, 'installedProposal' | 'effectiveDate'>;
}

const RenewalPortfolioCard: React.FC<ProductLineDataProps> = observer(({ productLineData, enrollmentPeriod }) => {
  const { id: productLineId, products } = productLineData;

  const isPortfolioWithSeveralProducts = products.length > 1;

  if (!hasRenewalRates(productLineData)) return null;

  const classes = createStyles({
    productsLength: products.length,
  });

  const exhibitUrls = enrollmentPeriod.installedProposal?.productLines.find(
    (line) => line.id === productLineId,
  )?.exhibitUrls;

  const downloadExhibit = async (): Promise<void> => {
    if (exhibitUrls) {
      const zipUrl = await getUploadExhibitZipUrl(exhibitUrls);
      if (zipUrl) {
        downloadByUrl(zipUrl);
      }
    }
  };

  const downloadButton = (
    <Button
      leftIconProp={<Download />}
      type="text"
      size="small"
      className={classes.downloadButton}
      color="primary"
      onClick={downloadExhibit}
      uppercase
      disabled={!exhibitUrls || exhibitUrls?.length === 0}
    >
      Download Renewal & Exhibits
    </Button>
  );

  const ratesItems = (imProductId: string, rates: InstalledRate[]): JSX.Element[] => {
    return rates
      .filter(({ renewalRate }) => renewalRate && renewalRate !== 'N/A')
      .map(({ rateStructure, renewalRate }) => {
        return (
          <li key={`${imProductId}-${rateStructure}`} className={classes.ratesItem}>
            <span>{rateStructure}</span>
            <span>{convertRateValue(renewalRate)}</span>
          </li>
        );
      });
  };

  const RenewalCardHeader = (
    <div className={classes.renewalProductHeader}>
      <p className={classes.renewalTag}>Not Yet Selected</p>
    </div>
  );

  const renewalProductsList = products.map(
    ({ carrierProductId, carrierProductName, renewalCarrierProductName, rates }) => {
      const productName = renewalCarrierProductName || carrierProductName;
      const filteredRates = rates.filter(({ renewalRate }) => renewalRate && renewalRate !== 'N/A');

      const RatesContent = (
        <div>
          {filteredRates.length > 0 ? (
            <ul className={classes.renewalRatesList}>{ratesItems(carrierProductId, rates)}</ul>
          ) : (
            <div className={classes.ratesMessage}>
              <InfoButton width="2rem" height="2rem" color={colors.red[600]} /> <span>Rates are unavailable</span>
            </div>
          )}
        </div>
      );

      if (isPortfolioWithSeveralProducts) {
        return (
          <div key={`${carrierProductId}_${productName}`} className={classes.multiProductWrapper}>
            <h3 className={classes.productTitle}>{productName}</h3>
            <div className={classes.multiProductRatesWrapper}>
              <p className={classes.ratesTitle}>Renewal Rates</p>
              {RatesContent}
            </div>
          </div>
        );
      }

      return (
        <div className={classes.renewalProductCard} key={`${carrierProductId}_${productName}`}>
          {RenewalCardHeader}
          <h3 className={clsx(classes.productTitle, classes.singleProductPortfolioTitle)}>{productName}</h3>
          <div className={classes.renewalCardContent}>
            <p className={classes.ratesTitle}>Renewal Rates</p>
            <ul className={classes.renewalRatesList}>{ratesItems(carrierProductId, rates)}</ul>
          </div>
          {downloadButton}
        </div>
      );
    },
  );

  return (
    <>
      {isPortfolioWithSeveralProducts ? (
        <div className={clsx(classes.renewalProductCard, classes.multiProductCard)}>
          {RenewalCardHeader}
          <div className={classes.productsList}>{renewalProductsList}</div>
          {downloadButton}
        </div>
      ) : (
        renewalProductsList
      )}
    </>
  );
});

export default RenewalPortfolioCard;
